import { useSyncExternalStore } from 'react';
import { fetchCurrentAdminAccess, type AdminAccessSnapshot } from '../api/admin-access';

const TOKEN_KEY = 'flashmate.admin.token';
const LEGACY_USER_KEY = 'flashmate.admin.user';

export type AdminAccessStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface AdminAuthSnapshot {
  authenticated: boolean;
  accessStatus: AdminAccessStatus;
  access: AdminAccessSnapshot | null;
  error: string | null;
}

let token = typeof window !== 'undefined' ? window.localStorage.getItem(TOKEN_KEY) : null;
let snapshot: AdminAuthSnapshot = {
  authenticated: Boolean(token),
  accessStatus: 'idle',
  access: null,
  error: null,
};
let accessRequest: Promise<AdminAccessSnapshot | null> | null = null;
const listeners = new Set<() => void>();

const notify = () => listeners.forEach((listener) => listener());
const replaceSnapshot = (next: AdminAuthSnapshot) => {
  snapshot = next;
  notify();
};

const clearBrowserSession = () => {
  if (typeof window === 'undefined') return;
  window.localStorage.removeItem(TOKEN_KEY);
  window.localStorage.removeItem(LEGACY_USER_KEY);
};

export const authStore = {
  getToken: () => token,
  getSnapshot: () => snapshot,
  isAuthenticated: () => snapshot.authenticated,
  getAccess: () => snapshot.access,
  signIn: (value: string, _untrustedLoginUser?: unknown) => {
    token = value;
    if (typeof window !== 'undefined') {
      window.localStorage.setItem(TOKEN_KEY, value);
      window.localStorage.removeItem(LEGACY_USER_KEY);
    }
    accessRequest = null;
    replaceSnapshot({ authenticated: true, accessStatus: 'idle', access: null, error: null });
  },
  hydrateAccess: (force = false): Promise<AdminAccessSnapshot | null> => {
    if (!token) return Promise.resolve(null);
    if (!force && snapshot.accessStatus === 'ready' && snapshot.access) {
      return Promise.resolve(snapshot.access);
    }
    if (!force && accessRequest) return accessRequest;

    const requestToken = token;
    replaceSnapshot({ ...snapshot, accessStatus: 'loading', error: null });
    accessRequest = fetchCurrentAdminAccess()
      .then((access) => {
        if (token !== requestToken) return null;
        replaceSnapshot({ authenticated: true, accessStatus: 'ready', access, error: null });
        return access;
      })
      .catch((cause: unknown) => {
        if (token !== requestToken) return null;
        const error = cause instanceof Error ? cause.message : '无法核验管理员权限';
        replaceSnapshot({ authenticated: true, accessStatus: 'error', access: null, error });
        throw cause;
      })
      .finally(() => {
        if (token === requestToken) accessRequest = null;
      });
    return accessRequest;
  },
  can: (roles?: readonly string[], permissions?: readonly string[]) => {
    if (!token || snapshot.accessStatus !== 'ready' || !snapshot.access) return false;
    const serverRoles = new Set([snapshot.access.role, ...snapshot.access.roleCodes].map((role) => role.toUpperCase()));
    const hasRole = !roles?.length || roles.some((role) => serverRoles.has(role.toUpperCase()));
    const effectivePermissions = new Set(snapshot.access.effectivePermissions);
    const hasPermissions = !permissions?.length || permissions.every((permission) => effectivePermissions.has(permission));
    return hasRole && hasPermissions;
  },
  canPermissions: (permissions?: readonly string[]) => authStore.can(undefined, permissions),
  signOut: () => {
    token = null;
    accessRequest = null;
    clearBrowserSession();
    replaceSnapshot({ authenticated: false, accessStatus: 'idle', access: null, error: null });
  },
  subscribe: (listener: () => void) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  },
};

const serverSnapshot: AdminAuthSnapshot = {
  authenticated: false,
  accessStatus: 'idle',
  access: null,
  error: null,
};

export const useAdminAuthState = () => useSyncExternalStore(authStore.subscribe, authStore.getSnapshot, () => serverSnapshot);
export const useAuth = () => useAdminAuthState().authenticated;
